import { useIsMobile } from '@/hooks/use-is-mobile';
import useAppConfig from '@rhg/hooks/use-app-config';
import usePlanPermissions from '@rhg/hooks/use-plan-permissions';
import commonMessages from '@rhg/lang/messages/common';
import { Button, ButtonProps, SxProps, Theme } from '@rhg/libs/material';
import {
    CareplanFilled,
    CareplanOutline,
    DiscoverFilled,
    DiscoverOutline,
    HomeFilled,
    HomeOutline,
    ProfileFilled,
    ProfileOutline,
    SearchActive,
    SearchOutline,
} from '@rhg/libs/material/icons';
import { routes } from '@rhg/utils/routes';
import Link from 'next/link';
import { useIntl } from 'react-intl';
import NavButton from '../nav-button';
import { navItemsMessages } from './messages';

const styleNavIcon = {
    width: 24,
    height: 24,
};
const styleSearchNavIcon = {
    width: 24,
    height: 24,
    // SVG itself is 20x20
    padding: '2px',
};

interface NavItemsProps {
    sx?: SxProps<Theme>;
    onClick?: () => void;
    testId?: string;
    addSyntheticIds?: boolean;
}

const NavItems = ({
    testId = 'NAV',
    addSyntheticIds,
    ...buttonProps
}: NavItemsProps & Partial<ButtonProps>) => {
    const { formatMessage } = useIntl();
    const { canView: canViewPlan } = usePlanPermissions();
    const isMobile = useIsMobile('lg');
    const { NEXT_PUBLIC_SEARCH_ENABLED: isSearchEnabled } = useAppConfig();
    const discoverInactiveIcon = isSearchEnabled ? (
        <SearchOutline sx={styleSearchNavIcon} />
    ) : (
        <DiscoverOutline sx={styleNavIcon} />
    );
    const discoverActiveIcon = isSearchEnabled ? (
        <SearchActive sx={styleSearchNavIcon} />
    ) : (
        <DiscoverFilled sx={styleNavIcon} />
    );

    return (
        <>
            <Link href={routes.dashboard} passHref legacyBehavior>
                <NavButton
                    startIcon={<HomeOutline sx={styleNavIcon} />}
                    activeStateIcon={<HomeFilled sx={styleNavIcon} />}
                    testId={testId}
                    trackButton="HomeTab"
                    altActiveHrefs={[routes.favorites, routes.recent]}
                    {...(addSyntheticIds
                        ? { 'data-syntheticid': 'NavHomeButton' }
                        : {})}
                    {...buttonProps}
                >
                    {formatMessage(commonMessages['common.page.home'])}
                </NavButton>
            </Link>
            {canViewPlan && (
                <Link href={routes.careplan} passHref legacyBehavior>
                    <NavButton
                        startIcon={<CareplanOutline sx={styleNavIcon} />}
                        activeStateIcon={<CareplanFilled sx={styleNavIcon} />}
                        testId={testId}
                        trackButton="PlanTab"
                        {...(addSyntheticIds
                            ? { 'data-syntheticid': 'NavPlanButton' }
                            : {})}
                        {...buttonProps}
                    >
                        {formatMessage(commonMessages['common.page.careplan'])}
                    </NavButton>
                </Link>
            )}
            <Link href={routes.discover} passHref legacyBehavior>
                <NavButton
                    startIcon={discoverInactiveIcon}
                    activeStateIcon={discoverActiveIcon}
                    testId={testId}
                    trackButton="DiscoverTab"
                    {...(addSyntheticIds
                        ? { 'data-syntheticid': 'NavDiscoverButton' }
                        : {})}
                    {...buttonProps}
                >
                    {formatMessage(commonMessages['common.page.discover'])}
                </NavButton>
            </Link>
            <Link href={routes.profile} passHref legacyBehavior>
                <NavButton
                    startIcon={<ProfileOutline sx={styleNavIcon} />}
                    activeStateIcon={<ProfileFilled sx={styleNavIcon} />}
                    altActiveHrefs={[routes.completed]}
                    trackButton="ProfileTab"
                    testId={testId}
                    {...(addSyntheticIds
                        ? { 'data-syntheticid': 'NavProfileButton' }
                        : {})}
                    {...buttonProps}
                >
                    {formatMessage(commonMessages['common.page.profile'])}
                </NavButton>
            </Link>
            {isMobile && (
                <Button
                    trackButton="UseMobileApp"
                    variant="outlined"
                    fullWidth
                    href={routes.external.appDownloadDeeplink}
                    sx={{ mt: 4 }}
                >
                    {formatMessage(navItemsMessages.useMobileAppCta)}
                </Button>
            )}
        </>
    );
};
export default NavItems;
